import React, { useMemo } from 'react';
import {
  Text as RNText,
  TextProps as RNTextProps,
  StyleSheet,
} from 'react-native';
import { useTheme } from '../themes';
import {
  useResponsiveTypography,
  useAccessibleTypography,
  useDynamicTextScale,
} from '../hooks/useTypography';
import {
  TypographyVariant,
  FontSize,
  AccessibilityTextScale,
  typographyTokens,
} from '../tokens/typography';
import { validateTextAccessibility } from '../utils/typography';

export interface TextProps extends Omit<RNTextProps, 'style'> {
  // Typography variant - determines the semantic meaning and default styling
  variant?: TypographyVariant;

  // Color options - can be theme color keys or custom hex values
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'inverse'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | string;

  // Text alignment
  align?: 'left' | 'center' | 'right' | 'justify';

  // Font weight override
  weight?:
    | 'thin'
    | 'extralight'
    | 'light'
    | 'normal'
    | 'medium'
    | 'semibold'
    | 'bold'
    | 'extrabold'
    | 'black';

  // Font size override (when not using variant)
  size?: FontSize;

  // Manual accessibility text scale override
  textScale?: AccessibilityTextScale;

  // Whether to respect user's dynamic text scaling preferences
  respectDynamicType?: boolean;

  // Whether to apply accessibility font weight adjustments
  respectBoldText?: boolean;

  // Maximum number of lines (for truncation)
  numberOfLines?: number;

  // Whether text should be selectable
  selectable?: boolean;

  // Custom style override
  style?: RNTextProps['style'];

  children: React.ReactNode;
}

export function Text({
  variant = 'body',
  color = 'primary',
  align = 'left',
  weight,
  size,
  textScale,
  respectDynamicType = true,
  respectBoldText = true,
  numberOfLines,
  selectable = false,
  style,
  children,
  ...textProps
}: TextProps) {
  const theme = useTheme();
  const { getVariantStyle, getScaledFontSize, getScaledLineHeight } =
    useResponsiveTypography();
  const { adjustFontWeight, accessibilityScale, isBoldTextEnabled } =
    useAccessibleTypography();
  const { textScale: dynamicTextScale } = useDynamicTextScale();

  // Determine effective text scale
  const effectiveTextScale =
    textScale || (respectDynamicType ? dynamicTextScale : accessibilityScale);

  // Get base typography style
  const baseStyle = useMemo(() => {
    if (size) {
      // When size is specified, create a custom style based on the size
      const fontSize = getScaledFontSize(size, effectiveTextScale);
      const lineHeight = getScaledLineHeight(size, effectiveTextScale);
      const letterSpacing = typographyTokens.fontSize[size].letterSpacing;

      return {
        fontFamily: getVariantStyle('body', effectiveTextScale).fontFamily, // Use body font family as default
        fontSize,
        fontWeight: weight
          ? typographyTokens.fontWeight[weight]
          : typographyTokens.fontWeight.normal,
        letterSpacing,
        lineHeight,
      };
    }
    return getVariantStyle(variant, effectiveTextScale);
  }, [
    variant,
    size,
    effectiveTextScale,
    getVariantStyle,
    getScaledFontSize,
    getScaledLineHeight,
    weight,
  ]);

  // Get text color
  const textColor = useMemo(() => {
    const themeColors = {
      primary: theme.theme.colors.text.primary,
      secondary: theme.theme.colors.text.secondary,
      tertiary: theme.theme.colors.text.tertiary,
      inverse: theme.theme.colors.text.inverse,
      success: '#16a34a', // Use hardcoded semantic colors for now
      warning: '#d97706',
      error: '#dc2626',
      info: '#2563eb',
    };

    return themeColors[color as keyof typeof themeColors] || color;
  }, [color, theme.theme.colors]);

  // Apply font weight adjustments for accessibility
  const effectiveFontWeight = useMemo(() => {
    const baseWeight = weight || baseStyle.fontWeight;
    return respectBoldText && isBoldTextEnabled
      ? adjustFontWeight(baseWeight as any)
      : baseWeight;
  }, [
    weight,
    baseStyle.fontWeight,
    respectBoldText,
    isBoldTextEnabled,
    adjustFontWeight,
  ]);

  // Validate accessibility if in development
  const accessibilityValidation = useMemo(() => {
    if (__DEV__) {
      return validateTextAccessibility(
        textColor,
        theme.theme.colors.background.primary,
        baseStyle.fontSize,
        effectiveFontWeight as string
      );
    }
    return null;
  }, [
    textColor,
    theme.theme.colors.background.primary,
    baseStyle.fontSize,
    effectiveFontWeight,
  ]);

  // Log accessibility warnings in development
  if (__DEV__ && accessibilityValidation && !accessibilityValidation.isValid) {
    console.warn(
      `Text accessibility warning: Contrast ratio ${accessibilityValidation.contrastRatio.toFixed(2)} ` +
        `does not meet WCAG standards. Consider adjusting colors.`
    );
  }

  // Combine all styles
  const combinedStyle = useMemo(
    () => [
      styles.base,
      {
        fontFamily: baseStyle.fontFamily,
        fontSize: baseStyle.fontSize,
        fontWeight: effectiveFontWeight as any, // Cast to satisfy React Native types
        letterSpacing: baseStyle.letterSpacing,
        lineHeight: baseStyle.lineHeight,
        color: textColor,
        textAlign: align,
      },
      style,
    ],
    [baseStyle, effectiveFontWeight, textColor, align, style]
  );

  return (
    <RNText
      {...textProps}
      style={combinedStyle}
      numberOfLines={numberOfLines}
      selectable={selectable}
      accessible={true}
      accessibilityRole='text'
    >
      {children}
    </RNText>
  );
}

const styles = StyleSheet.create({
  base: {
    // Base styles that ensure consistent rendering
    includeFontPadding: false, // Android-specific: removes extra padding
    textAlignVertical: 'center', // Android-specific: centers text vertically
  },
});

// Export specialized typography components
export * from './Typography';
